import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import EigooMark from "../components/EigooMark";
import {
    Headset,
    Zap,
    Shield,
    ArrowRight,
    Clock,
    CheckCircle2,
    LifeBuoy,
} from "lucide-react";

/**
 * Remote Support Hull
 * --------------------
 * Director directive 2026.02 · `/remote` is the canonical relative URL for
 * the integrated EIGOO-Command remote support entry point. Routes the
 * customer to the Foundry's Remote Support sub-rail (REMOTE-SUP-01/02).
 *
 * Two paths:
 *   1. Express Strike (60m / $180) — same-day RMM tunnel, recorded
 *   2. Sovereign Block (5h / $720) — block of pre-paid remote hours
 *
 * Both rails route via the public `/api/foundry/orders` intake (no auth
 * required) and auto-classify under the `Remote Support` category.
 */

const REMOTE_TIERS = [
    {
        code: "REMOTE-SUP-01",
        name: "Express Delivery",
        duration: "60 min",
        price: 180,
        sublabel: "Same-day · single incident",
        bullets: [
            "RMM tunnel, screenshare, recorded session",
            "L2 technician dispatched within the hour",
            "Single-incident scope, fixed price",
        ],
        accent: "hsl(45 85% 65%)",
    },
    {
        code: "REMOTE-SUP-02",
        name: "Sovereign Block",
        duration: "5 hours",
        price: 720,
        sublabel: "On-demand block · scheduled or live",
        bullets: [
            "Pre-paid block of remote support hours",
            "L2/L3 technician with priority routing",
            "Schedule or call-in any time within 30 days",
        ],
        accent: "hsl(40 75% 55%)",
        featured: true,
    },
];

export default function RemoteSupport() {
    const [tier, setTier] = useState("REMOTE-SUP-02");
    const [form, setForm] = useState({ name: "", email: "", brief: "" });
    const [busy, setBusy] = useState(false);
    const [confirmation, setConfirmation] = useState(null);

    useEffect(() => {
        document.title = "Remote Support · EIGOO Sovereign Platform";
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email required.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/foundry/orders", {
                client_email: form.email.trim(),
                client_name: form.name.trim(),
                service_code: tier,
                notes: form.brief.trim() || null,
            });
            setConfirmation(data);
            toast.success("Delivery commissioned.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not commission delivery.");
        } finally {
            setBusy(false);
        }
    };

    if (confirmation) {
        return (
            <div className="min-h-screen bg-background grain-overlay relative" data-testid="remote-success">
                <div aria-hidden="true" className="starfield fixed inset-0 pointer-events-none opacity-[0.18] z-0" />
                <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                    <EigooMark size="lg" sub="Remote Support" testid="remote-mark" />
                    <Link to="/" className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground">Return to Hub</Link>
                </header>
                <section className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 pt-12 pb-24 text-center fade-up">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(153_50%_25%)] border border-[hsl(153_50%_50%/0.5)] mb-6">
                        <CheckCircle2 size={28} className="text-[hsl(153_60%_70%)]" />
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">
                        Strike <span className="gold-gradient-text">commissioned.</span>
                    </h1>
                    <p className="mt-5 text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        A Field Operative is being matched to your delivery. Track progress with the
                        code below — bookmark it. The technician will reach out via the secure signal
                        channel on this order.
                    </p>
                    <div className="mt-8 inline-block glass rounded-xl px-6 py-4 text-left">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Tracking Code</div>
                        <div className="font-heading text-3xl gold-gradient-text font-mono mt-1" data-testid="remote-tracking-code">
                            {confirmation.tracking_code}
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">
                            {confirmation.service_name} · ${confirmation.amount?.toLocaleString()} · {confirmation.priority}
                        </div>
                    </div>
                    <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <Link
                            to={`/foundry/control?track=${confirmation.tracking_code}`}
                            data-testid="remote-track-link"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            Track delivery <ArrowRight size={16} />
                        </Link>
                        <button
                            onClick={() => { setConfirmation(null); setForm({ name: "", email: "", brief: "" }); }}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[hsl(40_50%_35%/0.4)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors"
                        >
                            Commission another
                        </button>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background grain-overlay relative" data-testid="remote-page">
            <div aria-hidden="true" className="starfield fixed inset-0 pointer-events-none opacity-[0.18] z-0" />

            <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <EigooMark size="lg" sub="Remote Support" testid="remote-mark" />
                <Link to="/" className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground">Return to Hub</Link>
            </header>

            <section className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-24">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-6">
                        <Headset size={11} className="text-[hsl(45_85%_62%)]" /> Remote Support · Sub-Rail
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        Get a sovereign tech
                        <span className="block gold-gradient-text mt-2">on the line — fast.</span>
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        Vetted Field Operatives. RMM tunnel, recorded sessions, and the
                        Sovereign Covenant rendered live on every delivery. Same-day Express
                        or pre-paid Sovereign Block — both route via the Foundry's Remote
                        Support rail.
                    </p>
                </div>

                {/* Tier selection */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="remote-tier-grid">
                    {REMOTE_TIERS.map((t) => {
                        const selected = tier === t.code;
                        return (
                            <button
                                key={t.code}
                                type="button"
                                onClick={() => setTier(t.code)}
                                data-testid={`remote-tier-${t.code}`}
                                className={`relative text-left glass rounded-2xl p-6 transition-all ${
                                    selected
                                        ? "border-[hsl(45_85%_68%)] shadow-[0_8px_30px_-10px_hsl(40_70%_45%/0.5)] -translate-y-0.5"
                                        : "border-[hsl(40_40%_25%/0.3)] hover:border-[hsl(45_85%_68%/0.5)]"
                                }`}
                                style={{ borderWidth: selected ? "2px" : "1px" }}
                            >
                                {t.featured && (
                                    <span className="absolute -top-3 left-6 text-[9px] uppercase tracking-[0.3em] px-2 py-1 rounded-full bg-[hsl(40_61%_25%)] text-[hsl(45_85%_75%)] border border-[hsl(45_85%_60%/0.4)]">
                                        Most chosen
                                    </span>
                                )}
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">
                                            {t.duration}
                                        </div>
                                        <div className="font-heading text-2xl mt-1 tracking-tight">{t.name}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{t.sublabel}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-heading text-3xl gold-gradient-text">${t.price}</div>
                                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">flat</div>
                                    </div>
                                </div>
                                <ul className="mt-5 space-y-2">
                                    {t.bullets.map((b) => (
                                        <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                                            <CheckCircle2 size={14} className="text-[hsl(153_55%_60%)] shrink-0 mt-0.5" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </button>
                        );
                    })}
                </div>

                {/* Intake form */}
                <form onSubmit={submit} className="mt-14 max-w-2xl glass rounded-2xl p-8" data-testid="remote-form">
                    <div className="flex items-baseline justify-between mb-5">
                        <div>
                            <div className="font-heading text-2xl tracking-tight">Commission your strike</div>
                            <div className="text-xs text-muted-foreground mt-1">A Field Operative will be matched within the hour.</div>
                        </div>
                        <Shield size={18} className="text-[hsl(45_85%_68%)]" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Your name</label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="remote-name"
                                placeholder="Jane Doe"
                                className="mt-2 w-full bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.3)] rounded-lg px-3 py-2.5 text-sm focus:border-[hsl(45_85%_68%)] focus:outline-none transition-colors"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="remote-email"
                                placeholder="jane@company.com"
                                className="mt-2 w-full bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.3)] rounded-lg px-3 py-2.5 text-sm focus:border-[hsl(45_85%_68%)] focus:outline-none transition-colors"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">What's broken? (optional)</label>
                        <textarea
                            value={form.brief}
                            onChange={(e) => setForm({ ...form, brief: e.target.value })}
                            data-testid="remote-brief"
                            placeholder="VPN dropping every 10 min on remote employees · Outlook crashes on launch · etc."
                            rows={3}
                            className="mt-2 w-full bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.3)] rounded-lg px-3 py-2.5 text-sm focus:border-[hsl(45_85%_68%)] focus:outline-none transition-colors resize-none"
                        />
                    </div>
                    <div className="mt-6 flex items-center justify-between flex-wrap gap-3 pt-5 border-t border-[hsl(40_40%_30%/0.18)]">
                        <div className="text-xs text-muted-foreground inline-flex items-center gap-2">
                            <Clock size={12} className="text-[hsl(45_85%_68%)]" />
                            Selected: <span className="text-foreground font-semibold">{REMOTE_TIERS.find(t => t.code === tier)?.name}</span> · ${REMOTE_TIERS.find(t => t.code === tier)?.price}
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="remote-submit"
                            className="group inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Commissioning..." : "Commission delivery"}
                            {!busy && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
                        </button>
                    </div>
                </form>

                <div className="mt-10 max-w-2xl flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                        <Zap size={12} className="text-[hsl(45_85%_68%)]" /> 60/30/10 covenant
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1.5">
                        <Shield size={12} className="text-[hsl(45_85%_68%)]" /> Recorded session
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1.5">
                        <LifeBuoy size={12} className="text-[hsl(45_85%_68%)]" /> Sovereign refund if not resolved
                    </span>
                </div>
            </section>

            <footer className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Remote Support · Same-day delivery fleet.</div>
                <div className="font-mono">/remote · Tier 1 covenant</div>
            </footer>
        </div>
    );
}
